/* ============================================================================
 * RESERVE_CAPITAL.EXE — token value and splits
 * ----------------------------------------------------------------------------
 * Ported from the design source (SupercomputeA/supercompute-immersion →
 * app-reserve-capital.html): value, the token split, and the reserve sheet.
 *
 * WHAT IS REAL: the split is held in this browser and refuses to save unless
 * the shares total exactly 100. WHAT IS NOT, AND SAYS SO: the price chart has
 * no feed, and the supply figures read "sample" — nothing is invented, and no
 * supply figure reaches this desk without a connected read node.
 * ========================================================================= */

import { useState } from 'react';
import { SkinArt } from '../components/SkinArt';

const TABS = [
  { id: 'value', label: 'Value' },
  { id: 'splits', label: 'Splits' },
  { id: 'reserve', label: 'Reserve' },
] as const;
type Tab = (typeof TABS)[number]['id'];

const SPLIT_DEFS = [
  { key: 'compute', label: 'Compute · fleet', short: 'compute' },
  { key: 'publishing', label: 'Publishing', short: 'publish' },
  { key: 'live', label: 'Live streaming', short: 'live' },
  { key: 'merch', label: 'Storefront · merch', short: 'merch' },
  { key: 'consulting', label: 'Consulting', short: 'consult' },
  { key: 'treasury', label: 'Treasury', short: 'treasury' },
] as const;

const DEFAULTS = [30, 22, 16, 14, 10, 8];

const SUPPLY_LINES = [
  { line: 'Total supply', note: 'Fixed at issue, never minted again' },
  { line: 'Circulating', note: 'What the market can reach today' },
  { line: 'Held by the desk', note: 'Treasury, untouched by this screen' },
];

const RESERVE_ROWS = [
  { holding: 'Operator wallet', sub: 'Fleet funds', where: 'Base', custody: 'self' },
  { holding: 'Card float', sub: 'Agentic commerce rail', where: 'AgentCard', custody: 'card issuer float' },
  { holding: 'Prepaid services', sub: 'Two hosting subscriptions', where: 'vendors', custody: 'vendor credit' },
];

const SPLIT_KEY = 'sc.split.reserve';

function readSplit(): number[] {
  try {
    const v = JSON.parse(localStorage.getItem(SPLIT_KEY) ?? 'null') as number[] | null;
    if (Array.isArray(v) && v.length === SPLIT_DEFS.length && v.every((n) => Number.isFinite(n))) return v;
  } catch {
    /* fall through */
  }
  return [...DEFAULTS];
}

export function ReserveCapital() {
  const [tab, setTab] = useState<Tab>('value');
  const [shares, setShares] = useState<number[]>(readSplit);
  const [saved, setSaved] = useState(false);
  const [feedOn, setFeedOn] = useState(false);

  const total = shares.reduce((a, b) => a + b, 0);
  const good = total === 100;

  const setShare = (i: number, v: string) => {
    const n = Math.max(0, Math.min(100, parseInt(v, 10) || 0));
    setShares((prev) => prev.map((s, j) => (j === i ? n : s)));
    setSaved(false);
  };

  const saveSplit = () => {
    if (!good) return;
    try {
      localStorage.setItem(SPLIT_KEY, JSON.stringify(shares));
    } catch {
      /* non-fatal */
    }
    setSaved(true);
  };

  const resetSplit = () => {
    setShares([...DEFAULTS]);
    setSaved(false);
  };

  return (
    <div className="dsp page">
      <header className="page__head">
        <SkinArt className="page__crest" src="/logos/dock-reserve-capital.png" width={40} height={40} alt="" aria-hidden="true" />
        <div className="page__titles">
          <p className="eyebrow">reserve · token value · splits</p>
          <h1 className="h1">Token value and splits</h1>
        </div>
        <div className="page__chips">
          <span className="badge badge-live"><span className="lamp" aria-hidden="true" />self-custody</span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="Reserve modules">
        {TABS.map((t) => (
          <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ VALUE ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'value'}>
        <div className="grid4">
          <div className="stat-tile"><span className="label">token value</span><span className="val">—</span><span className="sub">No feed connected. Nothing is invented.</span></div>
          <div className="stat-tile"><span className="label">supply lines</span><span className="val">3</span><span className="sub">Recorded in the ledger.</span></div>
          <div className="stat-tile"><span className="label">splits</span><span className="val">6</span><span className="sub">Written into the token.</span></div>
          <div className="stat-tile"><span className="label">chain</span><span className="val" style={{ fontSize: 15 }}>Base</span><span className="sub">Settlement layer.</span></div>
        </div>

        <div className="row row--between">
          <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>value · 30 days</p>
          <span className="badge badge-claude"><span className="lamp" style={{ background: 'var(--ui-dim)' }} aria-hidden="true" />no feed</span>
        </div>
        <div className="plot">
          <svg viewBox="0 0 640 150" role="img" aria-label="Empty price chart: axes drawn, no data feed connected.">
            <line className="axis" x1="40" y1="106" x2="620" y2="106" />
            <line className="axis" x1="40" y1="16" x2="40" y2="106" />
            <line className="axis" x1="40" y1="76" x2="620" y2="76" strokeDasharray="2 4" />
            <line className="axis" x1="40" y1="46" x2="620" y2="46" strokeDasharray="2 4" />
            <line className="axis-dash" x1="40" y1="88" x2="620" y2="88" />
            <text className="plot-note" x="46" y="82">awaiting price feed</text>
            <text className="plot-note" x="600" y="126" textAnchor="end">30 days</text>
            <text className="plot-note" x="40" y="126">-30d</text>
            <text className="plot-note" x="34" y="20" textAnchor="end">high</text>
            <text className="plot-note" x="34" y="106" textAnchor="end">low</text>
          </svg>
        </div>

        <div className="row row--between">
          <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>supply lines</p>
          <span className="mono">ledger · 3 rows</span>
        </div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Line</th><th className="r">Amount</th><th>Note</th><th>State</th></tr></thead>
            <tbody>
              {SUPPLY_LINES.map((s, i) => (
                <tr key={i}><td>{s.line}</td><td className="r mono">sample</td><td>{s.note}</td><td><span className="tag tag--muted">sample data</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="term row row--between">
          <div className="grow"><p className="micro micro--ink">Connect a price feed</p><p className="cap">Point the desk at a read node and this panel fills in.</p></div>
          <button className="switch" type="button" aria-pressed={feedOn} aria-label="Connect a price feed" onClick={() => setFeedOn((v) => !v)}>
            <span className="switch__track"><span className="switch__knob" /></span>
          </button>
        </div>
        {feedOn && <p className="cap">No read node wired on this device.</p>}
        <p className="cap">No invented price and no drawn trend — a chart with no feed stays empty. Amounts read "sample" on purpose: no supply figure reaches this desk without a connected read node.</p>
      </section>

      {/* ══ SPLITS ═════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'splits'}>
        <div className="grid2">
          <div className="stack">
            <div className="row row--between">
              <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>token splits</p>
              <span className="mono mono--ink" style={{ color: good ? 'var(--ui-ok)' : 'var(--ui-err)' }}>{total}%</span>
            </div>
            <p className="cap">The split is part of the token, not a promise made after it. Every settlement is divided the moment it lands, before it reaches a wallet. Shares must total exactly 100 — the desk will not save a split that does not add up.</p>
            <div className="stack">
              {SPLIT_DEFS.map((s, i) => (
                <div className="lrow" key={s.key}>
                  <span className="lrow__t">{s.label}</span>
                  <input className="field" type="number" min={0} max={100} step={1} aria-label={`${s.label} share`} style={{ width: 88 }} value={shares[i]} onChange={(e) => setShare(i, e.target.value)} />
                  <span className="mono">%</span>
                </div>
              ))}
            </div>
            {!good && <p className="err">Shares must total exactly 100 before this saves</p>}
            <div className="row">
              <button className="btn btn--gold" type="button" onClick={saveSplit} disabled={!good}>Save split</button>
              <button className="btn btn--ghost" type="button" onClick={resetSplit}>Reset to 100</button>
            </div>
          </div>
          <div className="stack">
            <div className="row row--between">
              <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>where it divides</p>
              <span className="tag tag--muted">sample data</span>
            </div>
            <div className="bars" role="img" aria-label={`Split bars, current shares: ${SPLIT_DEFS.map((s, i) => `${s.short} ${shares[i]}`).join(', ')}.`}>
              {SPLIT_DEFS.map((s, i) => (
                <div key={s.key}>
                  <i style={{ height: `${shares[i] === 0 ? 0 : Math.min(100, Math.max(2, shares[i] * 0.72))}%` }} />
                  <span>{s.short}</span>
                </div>
              ))}
            </div>
            <ul className="rows">
              <li className="item"><span className="tick" aria-hidden="true" /><span>Revenue loops are the recipients · <span className="mono">merch, tradedesk, publishing, live, consulting</span></span></li>
              <li className="item"><span className="tick" aria-hidden="true" /><span>The split is a rule, not a spreadsheet · <span className="mono">100% or it does not save</span></span></li>
            </ul>
            {saved && <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>Saved.</strong> The rule is held in this browser until it is written on chain.</span></p>}
          </div>
        </div>
      </section>

      {/* ══ RESERVE ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'reserve'}>
        <div className="grid3">
          <div className="stat-tile"><span className="label">holdings</span><span className="val">3</span><span className="sub">Where value actually sits.</span></div>
          <div className="stat-tile"><span className="label">custody</span><span className="val" style={{ fontSize: 15 }}>operator</span><span className="sub">No third party holds a key.</span></div>
          <div className="stat-tile"><span className="label">transfers</span><span className="val" style={{ fontSize: 15 }}>human</span><span className="sub">Signed off, then sent.</span></div>
        </div>
        <div className="row row--between">
          <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>the reserve</p>
          <span className="tag tag--muted">as configured</span>
        </div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Holding</th><th>Where</th><th>Custody</th><th>State</th></tr></thead>
            <tbody>
              {RESERVE_ROWS.map((r, i) => (
                <tr key={i}><td>{r.holding}<span className="sub">{r.sub}</span></td><td className="mono">{r.where}</td><td>{r.custody}</td><td><span className="badge badge-live"><span className="lamp" aria-hidden="true" />active</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="notice notice--brass"><span className="tick" aria-hidden="true" /><span><strong>Human gate.</strong> This desk reads the reserve and reports on it. It cannot move a single unit — transfers are prepared here and signed off in chat.</span></p>
        <div className="row row--between"><button className="btn btn--ghost" type="button">Copy reserve id</button><span className="cap">Read-only view.</span></div>
      </section>

      <p className="page__foot">Token value answers one question honestly: what is the token worth, and how does it divide.</p>
    </div>
  );
}
